// Execution-splitting facade (Plan 18 §4.4 — the Plan 20 seam; interfaces are
// FROZEN). One `scanDocument` pass — always `compoundBlockAware: true` (postgres
// self-excludes via blockTrackingEngine) — assembled into ordered executable
// units: per-statement for mysql/sqlite/pg/oracle, per-GO-batch for mssql.
// Electron adapters import this directly across the boundary (Decision 1 —
// pure TS, wasm-free). Ambiguity parity with the legacy
// electron/query-engine-adapters/splitSqlStatements.ts fallback (Decision 4):
// an unterminated string/comment/dollar-quote returns the whole trimmed buffer
// as ONE unit with `ambiguous: true`.

import type { ParseOptions, SchemaEffect, SqlEngine, StatementKind, StatementRisk } from '../model';
import { classifyStatement } from './classifyStatement';
import { extractSchemaEffects } from './schemaEffects';
import { scanDocument, type ScanBatchSeparator, type ScanResult, type ScanStatement } from './scanner';

export interface ExecutableStatement {
  readonly text: string;
  /** Offset into the ORIGINAL buffer — sqliteAdapter's error mapping consumes these. */
  readonly startOffset: number;
  readonly endOffset: number;
  readonly kind: StatementKind;
  readonly risk: StatementRisk;
  readonly schemaEffects: SchemaEffect[];
}

export interface ExecutableUnit {
  /** Unit text to send to the engine. */
  readonly sql: string;
  readonly statements: ExecutableStatement[];
  readonly separator: 'GO' | '/' | 'none';
  /** 1 unless `GO N`; capped at 100_000 (Decision 7 — mssqlSplitGo's MAX_GO_REPEATS). */
  readonly goCount: number;
  /**
   * Index of this unit's batch in `getDocumentStructure(...).batches` — NOT the
   * caller's flat execution-loop index. The two diverge because execution drops
   * EVERY statement-less batch (nothing to run) while `assembleBatches`
   * (structure.ts:102) KEEPS a statement-less batch that has a separator (e.g.
   * an SSMS-style header comment followed by GO), shifting all later batches.
   * Error-location mapping (`resolveGoBatchErrorLocation`) indexes
   * `structure.batches`, so it must receive THIS index — the raw loop index
   * squiggles the wrong line (Plan 20 §10 step 1 review, Important #1).
   * Additive field on the frozen Plan 18 seam; existing consumers unaffected.
   */
  readonly structureBatchIndex: number;
}

export interface ExecutionSplit {
  readonly units: ExecutableUnit[];
  /** Decision 4 — whole buffer returned as one unit. */
  readonly ambiguous: boolean;
  /** mysql: >0 ⇒ the buffer used DELIMITER (the adapter rewrite path consumes this). */
  readonly delimiterFlipCount: number;
}

// Mirrors mssqlSplitGo's consumeGoLine: `GO 0` (or absent N) → 1; upper-capped.
const MAX_GO_REPEATS = 100_000;
function clampGoCount(sep: ScanBatchSeparator | undefined): number {
  if (!sep || sep.goCount <= 0) return 1;
  return Math.min(sep.goCount, MAX_GO_REPEATS);
}

/**
 * Raw scan batchIndex → index in `getDocumentStructure(...).batches`.
 *
 * Mirrors `assembleBatches`' keep-rule (structure.ts:102): a batch survives
 * into `structure.batches` when it has statements OR a separator — including a
 * DELIMITER client-directive injected as a statement (structure.ts:78-92,
 * mysql only). Execution builders additionally drop statement-less batches, so
 * the two index spaces diverge on comment-only GO batches; this map bridges
 * them (`ExecutableUnit.structureBatchIndex`).
 */
function buildStructureIndexByBatch(scan: ScanResult): Map<number, number> {
  const populated = new Set<number>();
  for (const s of scan.statements) populated.add(s.batchIndex);
  for (const flip of scan.delimiterFlips) {
    const prior = scan.statements.filter((s) => s.endOffset <= flip.startOffset);
    populated.add(prior.length > 0 ? prior[prior.length - 1].batchIndex : 0);
  }
  for (const sep of scan.batchSeparators) populated.add(sep.batchIndex);

  const map = new Map<number, number>();
  let structureIndex = 0;
  for (let bi = 0; bi <= scan.maxBatchIndex; bi++) {
    if (!populated.has(bi)) continue;
    map.set(bi, structureIndex++);
  }
  return map;
}

function toExecutable(s: ScanStatement, engine: SqlEngine): ExecutableStatement {
  const { kind, risk } = classifyStatement(s.text, engine);
  return {
    text: s.text,
    startOffset: s.startOffset,
    endOffset: s.endOffset,
    kind,
    risk,
    schemaEffects: extractSchemaEffects(s.text, engine),
  };
}

// mssql: one unit per GO batch — the batch's statements listed, `GO N` exposed
// as a NUMBER (never N duplicated units; Plan 20's execute loop needs the count
// for cancellation between repeats). Empty batches (bare leading GO, GO GO) are
// dropped, matching splitMssqlBatches.
function buildBatchUnits(
  sql: string,
  statements: ScanStatement[],
  separators: ScanBatchSeparator[],
  maxBatchIndex: number,
  engine: SqlEngine,
  structureIndexByBatch: Map<number, number>,
): ExecutableUnit[] {
  const sepByBatch = new Map<number, ScanBatchSeparator>();
  for (const sep of separators) sepByBatch.set(sep.batchIndex, sep);
  const stmtsByBatch = new Map<number, ScanStatement[]>();
  for (const s of statements) {
    const list = stmtsByBatch.get(s.batchIndex);
    if (list) list.push(s); else stmtsByBatch.set(s.batchIndex, [s]);
  }

  const units: ExecutableUnit[] = [];
  for (let bi = 0; bi <= maxBatchIndex; bi++) {
    const stmts = stmtsByBatch.get(bi) ?? [];
    if (stmts.length === 0) continue;
    const sep = sepByBatch.get(bi);
    // §4.6 T9 fix: for the trailing batch (no following GO/`GO N`), the end
    // boundary must be `sql.length`, NOT the last statement's own `endOffset`.
    // `emitStatement` (scanner.ts) computes `endOffset` EXCLUSIVE of the `;`
    // that terminated it (the `;` is consumed separately, after the
    // `emitStatement(i)` call) — that's correct for `ScanStatement.text` (a
    // bare statement string never needs its own terminator), but using it
    // here silently dropped the trailing `;` from the reconstructed batch
    // text whenever the last statement in a GO-less script (or the tail
    // after the final GO) had one. Harmless for most statements (SQL Server
    // accepts a final statement without a trailing `;`), but T-SQL REQUIRES
    // one on MERGE — the missing `;` surfaced as engine error 10713 ("A
    // MERGE statement must be terminated by a semi-colon") even though the
    // ORIGINAL script text plainly had it. `sql.length` naturally includes
    // the terminator (and any trailing comment/whitespace, trimmed below) —
    // matches the `sep`-defined branch, which already slices into the raw
    // source up to the separator and so already preserves it.
    const contentEnd = sep ? sep.startOffset : sql.length;
    units.push({
      sql: sql.slice(stmts[0].startOffset, contentEnd).trim(),
      statements: stmts.map((s) => toExecutable(s, engine)),
      separator: sep ? sep.kind : 'none',
      goCount: clampGoCount(sep),
      // A populated batch is always kept by the structure keep-rule, so the
      // map always has an entry here; `?? 0` only satisfies the type.
      structureBatchIndex: structureIndexByBatch.get(bi) ?? 0,
    });
  }
  return units;
}

// mysql/sqlite/pg/oracle: one unit per statement. Only oracle produces batch
// separators (`/`); the closing separator is carried by the LAST statement of
// its batch so callers can distinguish `/`-terminated PL/SQL blocks.
function buildStatementUnits(
  statements: ScanStatement[],
  separators: ScanBatchSeparator[],
  engine: SqlEngine,
  structureIndexByBatch: Map<number, number>,
): ExecutableUnit[] {
  const sepByBatch = new Map<number, ScanBatchSeparator>();
  for (const sep of separators) sepByBatch.set(sep.batchIndex, sep);

  return statements.map((s, i) => {
    const next = statements[i + 1];
    const lastInBatch = !next || next.batchIndex !== s.batchIndex;
    const sep = lastInBatch ? sepByBatch.get(s.batchIndex) : undefined;
    return {
      sql: s.text,
      statements: [toExecutable(s, engine)],
      separator: sep ? sep.kind : 'none',
      goCount: 1,
      structureBatchIndex: structureIndexByBatch.get(s.batchIndex) ?? 0,
    };
  });
}

export function splitForExecution(sql: string, options: ParseOptions): ExecutionSplit {
  const engine = options.engine;
  // ONE scan pass total. Always compound-block-aware: routine/trigger bodies
  // keep their inner `;` (postgres self-excludes — dollar-quote protected).
  // `treatBareBeginAsRoutineBody` is force-OFF here too — it's a VQE-only,
  // additive opt-in (EDG-classification) that regroups a bare `BEGIN…END`
  // into one statement; execution batching must never depend on caller
  // discipline for that isolation, so this choke point strips the flag
  // by construction, mirroring the `compoundBlockAware` override above.
  const scan = scanDocument(sql, { ...options, compoundBlockAware: true, treatBareBeginAsRoutineBody: false });
  const delimiterFlipCount = scan.delimiterFlips.length;

  if (scan.unterminatedAtEof) {
    // Conservative legacy parity: don't attempt to split — hand the engine the
    // whole buffer so it can surface a proper error.
    const trimmed = sql.trim();
    if (!trimmed) return { units: [], ambiguous: true, delimiterFlipCount };
    const startOffset = sql.length - sql.trimStart().length;
    const whole: ScanStatement = {
      text: trimmed,
      startOffset,
      endOffset: startOffset + trimmed.length,
      batchIndex: 0,
    };
    return {
      units: [{
        sql: trimmed,
        statements: [toExecutable(whole, engine)],
        separator: 'none',
        goCount: 1,
        structureBatchIndex: 0,
      }],
      ambiguous: true,
      delimiterFlipCount,
    };
  }

  const structureIndexByBatch = buildStructureIndexByBatch(scan);
  const units = engine === 'mssql'
    ? buildBatchUnits(sql, scan.statements, scan.batchSeparators, scan.maxBatchIndex, engine, structureIndexByBatch)
    : buildStatementUnits(scan.statements, scan.batchSeparators, engine, structureIndexByBatch);
  return { units, ambiguous: false, delimiterFlipCount };
}
